import {
  Component,
  ChangeDetectorRef,
  ElementRef,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Subscription, combineLatest } from 'rxjs';

import { ReportQuery, ReportQueryService, ReportWidgetData } from '../../../services/report-query';
import { DashboardWidgetLayout } from '../dashboard-grid/dashboard-grid';
import {
  DashboardTimeWindowOption,
  DashboardValueFormat,
  dashboardErrorMessage,
  dashboardLayoutStyle,
  dashboardWindowQuery,
  formatDashboardValue,
  loadDashboardScalar,
} from '../../widget-and-graphs.models';

type ApexChartInstance = { render(): Promise<void>; destroy(): void };
type ApexChartConstructor = new (element: HTMLElement, options: Record<string, unknown>) => ApexChartInstance;

export type DashboardGraphType = 'line' | 'area' | 'bar';
export type DashboardGraphCurve = 'smooth' | 'straight' | 'stepline';

export interface DashboardGraphQuery {
  name: string;
  query: ReportQuery;
  type?: DashboardGraphType;
  color?: string;
  lineWidth?: number;
  dashed?: boolean;
  fillOpacity?: number;
  showMarkers?: boolean;
  valueMultiplier?: number;
}

interface ApexSeries {
  name: string;
  type: 'line' | 'area' | 'column';
  data: Array<{ x: string; y: number }>;
  color?: string;
  lineWidth?: number;
  dashed?: boolean;
  fillOpacity?: number;
  showMarkers?: boolean;
}

@Component({
  selector: 'app-dashboard-graph-widget',
  standalone: false,
  templateUrl: './dashboard-graph-widget.html',
  styleUrl: './dashboard-graph-widget.scss',
})
export class DashboardGraphWidget implements OnChanges, OnDestroy {
  @Input() iconSvg = '';
  @Input({ required: true }) title = '';
  @Input({ required: true }) valueQuery!: ReportQuery;
  @Input({ required: true }) graphQueries: DashboardGraphQuery[] = [];
  @Input() valueFormat: DashboardValueFormat = 'number';
  @Input() valueMultiplier = 1;
  @Input() currencyCode = 'USD';
  @Input() locale = 'en-US';
  @Input() graphPadding = false;
  @Input() showAxes = false;
  @Input() chartHeight = 96;
  @Input() curve: DashboardGraphCurve = 'smooth';
  @Input() showTimeWindowPicker = true;
  @Input() fixedTimeWindowLabel = '';
  @Input() timeWindowOptions: DashboardTimeWindowOption[] = [
    { label: 'Last 7 days', duration: 7 },
    { label: 'Last 30 days', duration: 30 },
    { label: 'Last 3 months', duration: 90 },
  ];
  @Input() layout: DashboardWidgetLayout = { xs: { width: 12, height: 4 } };

  loading = false;
  error = '';
  formattedValue = '0';
  change = 0;
  rangeOpen = false;
  selectedWindowIndex = 0;
  series: ApexSeries[] = [];

  private request?: Subscription;
  private chart?: ApexChartInstance;
  private chartHost?: HTMLElement;

  constructor(
    private readonly reportQuery: ReportQueryService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  @HostBinding('attr.style')
  get layoutStyles(): string {
    return dashboardLayoutStyle(this.layout);
  }

  @ViewChild('chartHost')
  set chartElement(element: ElementRef<HTMLElement> | undefined) {
    this.chartHost = element?.nativeElement;
    if (this.chartHost && this.series.length) queueMicrotask(() => this.renderChart());
  }

  get selectedWindow(): DashboardTimeWindowOption {
    return this.timeWindowOptions[this.selectedWindowIndex]
      ?? { label: this.fixedTimeWindowLabel || 'Selected period', duration: this.valueQuery?.duration ?? 30 };
  }

  get displayedWindowLabel(): string {
    return this.showTimeWindowPicker
      ? this.selectedWindow.label
      : this.fixedTimeWindowLabel || this.selectedWindow.label;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['valueQuery'] || changes['graphQueries'] || changes['timeWindowOptions'] || changes['valueMultiplier']) {
      const configuredDuration = this.valueQuery?.duration;
      const match = this.timeWindowOptions.findIndex((option) => option.duration === configuredDuration);
      this.selectedWindowIndex = match >= 0 ? match : 0;
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.request?.unsubscribe();
    this.chart?.destroy();
  }

  selectWindow(index: number): void {
    this.selectedWindowIndex = index;
    this.rangeOpen = false;
    this.load();
  }

  private load(): void {
    this.request?.unsubscribe();
    this.chart?.destroy();
    this.chart = undefined;
    this.series = [];
    this.error = '';
    if (!this.valueQuery || this.graphQueries.length === 0) {
      this.error = 'A dashboard graph requires a value query and at least one graph query.';
      return;
    }

    const option = this.selectedWindow;
    const valueQuery = this.showTimeWindowPicker
      ? dashboardWindowQuery(this.reportQuery, this.valueQuery, option)
      : this.valueQuery;
    const graphQueries = this.graphQueries.map((definition) => ({
      ...definition,
      query: this.showTimeWindowPicker
        ? dashboardWindowQuery(this.reportQuery, definition.query, option)
        : definition.query,
    }));

    this.loading = true;
    this.request = combineLatest([
      loadDashboardScalar(this.reportQuery, valueQuery, 'number'),
      ...graphQueries.map((definition) => this.reportQuery.loadWidget(definition.query, 'time_series')),
    ]).subscribe({
      next: ([scalar, ...reports]) => {
        this.formattedValue = formatDashboardValue(
          Number(scalar.value) * this.valueMultiplier,
          this.valueFormat,
          this.currencyCode,
          this.locale,
        );
        this.series = reports.flatMap((report, index) =>
          this.toSeries(report as ReportWidgetData, graphQueries[index]),
        );
        this.change = this.calculateChange(this.series[0]?.data ?? []);
        this.loading = false;
        this.cdr.detectChanges();
        queueMicrotask(() => this.renderChart());
      },
      error: (error) => {
        this.error = dashboardErrorMessage(error);
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  private toSeries(report: ReportWidgetData, definition: DashboardGraphQuery): ApexSeries[] {
    const bindings = report.verification.bindings;
    const category = bindings.category;
    if (!category) return [];
    const type = definition.type === 'bar' ? 'column' : definition.type ?? 'line';
    const label = (key: string, value: unknown) => {
      const coerced = report.verification.coercions.some((item) => item.key === key);
      return coerced || typeof value !== 'string' ? String(value ?? '') : value;
    };
    if (bindings.series_by && bindings.series.length === 1) {
      const measure = bindings.series[0];
      const grouped = new Map<string, Array<{ x: string; y: number }>>();
      report.result.rows.forEach((row) => {
        const name = label(bindings.series_by!, row[bindings.series_by!]);
        const points = grouped.get(name) ?? [];
        points.push({ x: label(category, row[category]), y: this.number(row[measure]) * (definition.valueMultiplier ?? 1) });
        grouped.set(name, points);
      });
      return Array.from(grouped, ([name, data]) => this.styledSeries(
        `${definition.name}: ${name}`,
        type,
        this.fillDailyTimeline(data, report, definition),
        definition,
      ));
    }
    return bindings.series.map((measure) => this.styledSeries(
      bindings.series.length === 1
        ? definition.name
        : `${definition.name}: ${report.result.columns.find((column) => column.key === measure)?.label ?? measure}`,
      type,
      this.fillDailyTimeline(report.result.rows.map((row) => ({
        x: label(category, row[category]),
        y: this.number(row[measure]) * (definition.valueMultiplier ?? 1),
      })), report, definition),
      definition,
    ));
  }

  private renderChart(): void {
    if (!this.chartHost || !this.series.length) return;
    const ApexCharts = (window as unknown as { ApexCharts?: ApexChartConstructor }).ApexCharts;
    if (!ApexCharts) {
      this.error = 'Tabler ApexCharts is not loaded.';
      this.cdr.detectChanges();
      return;
    }
    this.chart?.destroy();
    const colors = this.series.map((series, index) => series.color ?? this.defaultColor(index));
    this.chart = new ApexCharts(this.chartHost, {
      chart: {
        type: 'line',
        stacked: false,
        height: this.chartHeight,
        fontFamily: 'inherit',
        parentHeightOffset: 0,
        animations: { enabled: false },
        toolbar: { show: false },
        sparkline: { enabled: !this.showAxes },
      },
      series: this.series,
      colors,
      stroke: {
        curve: this.series.some((series) => series.dashed) ? 'smooth' : this.curve,
        lineCap: 'round',
        width: this.series.map((series) => series.dashed ? 1 : (series.lineWidth ?? 2)),
        dashArray: this.series.map((series) => series.dashed ? 5 : 0),
      },
      fill: {
        opacity: this.series.map((series) =>
          series.fillOpacity ?? (series.type === 'area' ? 0.2 : 1),
        ),
      },
      markers: {
        size: this.series.map((series) => !series.dashed && series.showMarkers ? 3 : 0),
        strokeWidth: 0,
        hover: { sizeOffset: 2 },
      },
      plotOptions: { bar: { columnWidth: '55%', borderRadius: 2 } },
      grid: {
        padding: this.graphPadding
          ? { left: 8, right: 8, top: 4, bottom: 4 }
          : { left: 0, right: 0, top: 0, bottom: 0 },
      },
      dataLabels: { enabled: false },
      legend: { show: this.showAxes, position: 'bottom', labels: {colors:'var(--tblr-body-color)'} },
      xaxis: { type: this.isQuarterly ? 'category' : 'datetime', tooltip: { enabled: false }, labels: {style:{colors:'var(--tblr-secondary-color)'}} },
      yaxis: { show: this.showAxes, labels: {style:{colors:'var(--tblr-secondary-color)'}} },
      tooltip: { theme: 'dark', x: this.isQuarterly ? {formatter:(value: unknown)=>String(value)} : { format: 'dd MMM' },
        ...(this.showAxes ? {y:{formatter:(value:number)=>formatDashboardValue(value,this.valueFormat,this.currencyCode,this.locale)}} : {}) },
    });
    void this.chart.render();
  }

  private styledSeries(
    name: string,
    type: ApexSeries['type'],
    data: ApexSeries['data'],
    definition: DashboardGraphQuery,
  ): ApexSeries {
    return {
      name,
      type,
      data: this.isQuarterly ? data.map(point => {
        const date = new Date(point.x);
        return {...point, x: `Q${Math.floor(date.getUTCMonth() / 3) + 1} ${date.getUTCFullYear()}`};
      }) : data,
      color: definition.color,
      lineWidth: definition.lineWidth,
      dashed: definition.dashed,
      fillOpacity: definition.fillOpacity,
      showMarkers: definition.showMarkers,
    };
  }

  private fillDailyTimeline(
    points: ApexSeries['data'],
    report: ReportWidgetData,
    definition: DashboardGraphQuery,
  ): ApexSeries['data'] {
    const isDaily = definition.query.group_by?.some((group) => group.bucket === 'day');
    const isQuarterly = definition.query.group_by?.some((group) => group.bucket === 'quarter');
    if (!isDaily && !isQuarterly) return points;

    const from = new Date(report.verification.time_window.from);
    const until = new Date(report.verification.time_window.until);
    if (!Number.isFinite(from.getTime()) || !Number.isFinite(until.getTime())) return points;

    const values = new Map(points.map((point) => [this.utcDay(point.x), point.y]));
    const timeline: ApexSeries['data'] = [];
    const cursor = isQuarterly ? new Date(Date.UTC(from.getUTCFullYear(), Math.floor(from.getUTCMonth() / 3) * 3, 1)) : new Date(from);
    for (; cursor < until; isQuarterly ? cursor.setUTCMonth(cursor.getUTCMonth() + 3) : cursor.setUTCDate(cursor.getUTCDate() + 1)) {
      const day = this.utcDay(cursor.toISOString());
      timeline.push({ x: `${day}T00:00:00.000Z`, y: values.get(day) ?? 0 });
    }
    return timeline;
  }

  private get isQuarterly(): boolean {
    return this.graphQueries.length > 0 && this.graphQueries.every(item => item.query.group_by?.some(group => group.bucket === 'quarter'));
  }

  private utcDay(value: string): string {
    const parsed = new Date(value);
    return Number.isFinite(parsed.getTime()) ? parsed.toISOString().slice(0, 10) : value.slice(0, 10);
  }

  private calculateChange(points: Array<{ x: string; y: number }>): number {
    if (points.length < 2) return 0;
    const first = points[0].y;
    const last = points[points.length - 1].y;
    return first === 0 ? 0 : ((last - first) / Math.abs(first)) * 100;
  }

  private number(value: unknown): number {
    const parsed = typeof value === 'number' ? value : Number(value ?? 0);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private defaultColor(index: number): string {
    return ['#206bc4', '#2fb344', '#f59f00', '#d63939', '#ae3ec9'][index % 5];
  }
}
